'use client'

import { useEffect, useRef, useState } from 'react'

// The sticky bar on /clients: one chip per industry section (a plain anchor,
// so it jumps there even before JS loads), the chip of the section in view
// highlighted, and a switch that shows only clients with a case study. The
// switch sets data-filter on #client-groups; client-story.css does the hiding.

export default function ClientsFilterBar({ groups, total, storyCount }) {
  const [active, setActive] = useState('')
  const [storiesOnly, setStoriesOnly] = useState(false)
  const chipsRef = useRef(null)

  useEffect(() => {
    const list = document.getElementById('client-groups')
    if (list) list.dataset.filter = storiesOnly ? 'stories' : 'all'
  }, [storiesOnly])

  // Highlight the section crossing the upper third of the screen.
  useEffect(() => {
    const sections = groups.map((group) => document.getElementById(group.id)).filter(Boolean)
    const observer = new IntersectionObserver(
      (entries) => {
        const hit = entries.find((entry) => entry.isIntersecting)
        if (hit) setActive(hit.target.id)
        // Scrolled back up above the first section: "All" again.
        else if (entries.some((entry) => entry.target === sections[0] && entry.boundingClientRect.top > 0)) setActive('')
      },
      { rootMargin: '-35% 0px -60% 0px' },
    )
    sections.forEach((section) => observer.observe(section))
    return () => observer.disconnect()
  }, [groups])

  // Keep the active chip visible in the row on narrow screens, without
  // scrolling the page itself.
  useEffect(() => {
    const row = chipsRef.current
    const chip = row?.querySelector('[aria-current="true"]')
    if (!row || !chip) return
    const left = chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2
    row.scrollTo({ left: Math.max(0, left), behavior: 'smooth' })
  }, [active])

  const shown = storiesOnly ? groups.filter((group) => group.stories > 0) : groups

  return (
    <nav className="cs-filter" aria-label="Jump to an industry">
      <div className="container cs-filter-inner">
        <div className="cs-filter-chips" ref={chipsRef}>
          <a href="#client-groups" className="cs-chip" aria-current={active === '' ? 'true' : undefined} onClick={() => setActive('')}>
            All <span>{storiesOnly ? storyCount : total}</span>
          </a>
          {shown.map((group) => (
            <a
              key={group.id}
              href={`#${group.id}`}
              className="cs-chip"
              aria-current={active === group.id ? 'true' : undefined}
              onClick={() => setActive(group.id)}
            >
              {group.label} <span>{storiesOnly ? group.stories : group.count}</span>
            </a>
          ))}
        </div>
        <button
          type="button"
          className="cs-switch"
          role="switch"
          aria-checked={storiesOnly}
          onClick={() => setStoriesOnly((on) => !on)}
        >
          <span className="cs-switch-track" aria-hidden="true"><span /></span>
          Case studies only
        </button>
      </div>
    </nav>
  )
}
